import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/i18n/translations";

const I18nContext = createContext(null);

function detectLang() {
  const saved = localStorage.getItem("nx_lang");
  if (saved === "en" || saved === "ar") return saved;
  const nav = (navigator.language || "ar").toLowerCase();
  // DAAM is Arabic-first: default to Arabic unless the browser explicitly prefers English
  return nav.startsWith("en") ? "en" : "ar";
}

function detectTheme() {
  const saved = localStorage.getItem("nx_theme");
  if (saved === "light" || saved === "dark") return saved;
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(detectLang);
  const [theme, setTheme] = useState(detectTheme);

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("lang", lang);
    html.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
    localStorage.setItem("nx_lang", lang);
  }, [lang]);

  useEffect(() => {
    const html = document.documentElement;
    html.classList.toggle("dark", theme === "dark");
    localStorage.setItem("nx_theme", theme);
  }, [theme]);

  const t = useMemo(() => translations[lang], [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      theme,
      setTheme,
      toggleLang: () => setLang((l) => (l === "en" ? "ar" : "en")),
      toggleTheme: () => setTheme((th) => (th === "dark" ? "light" : "dark")),
      t,
      dir: lang === "ar" ? "rtl" : "ltr",
    }),
    [lang, theme, t]
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
};
